import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { RestartAlt as ResetIcon } from '@mui/icons-material';
import useApi from '../../hooks/useApi';
import { AiCostGrid, TokenUsageBar, useErrorToast } from '../../components/common';
import { useCustomAlert } from '../../components/common/CustomAlert';
import { getApiErrorMessage } from '../../utils/errorMessages';
import {
  fetchAiUsage,
  resetAiUsageCycle,
  saveClientTokenLimit,
} from '../../services/aiGovernanceService';
import {
  USAGE_SEVERITY_COLORS,
  formatCost,
  formatTokens,
  getSummarySeverity,
} from '../../constants/aiUsage';
import AiTokenLimitFields from './AiTokenLimitFields';
import {
  buildTokenLimitPayload,
  emptyTokenLimitValues,
  tokenLimitValuesFromApi,
  validateTokenLimitValues,
} from './aiTokenLimitForm';

/**
 * JLL Super Admin view of one client's AI consumption, with the controls to act
 * on it: raise the allowance, change the alert threshold, switch enforcement on
 * or off, and start a fresh allowance cycle.
 *
 * Reuses the same meter and cost grid the client's own users see, so JLL and the
 * client are always looking at the same numbers.
 */
const ClientAiUsageDialog = ({ open, client, onClose, onSaved }) => {
  const api = useApi();
  const { showErrorToast } = useErrorToast();
  const { showConfirm } = useCustomAlert();

  const [summary, setSummary] = useState(null);
  const [byModel, setByModel] = useState([]);
  const [limitValues, setLimitValues] = useState(emptyTokenLimitValues());
  const [limitErrors, setLimitErrors] = useState({});
  const [allTime, setAllTime] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState(null);

  const tenantId = client?.TenantId;

  const load = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const usage = await fetchAiUsage(api, { tenantId, allTime });
      setSummary(usage.summary);
      setByModel(usage.byModel);
      setLimitValues(tokenLimitValuesFromApi(usage.summary));
      setLimitErrors({});
    } catch (err) {
      console.error('Error loading client AI usage:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to load AI usage for this client'));
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, allTime, showErrorToast]);

  useEffect(() => {
    if (open && tenantId) {
      setNotice(null);
      load();
    }
  }, [open, tenantId, load]);

  useEffect(() => {
    if (!open) {
      setSummary(null);
      setByModel([]);
      setAllTime(false);
    }
  }, [open]);

  const severity = useMemo(() => getSummarySeverity(summary), [summary]);
  const severityColor = USAGE_SEVERITY_COLORS[severity];
  const currency = summary?.currency || 'USD';

  const handleLimitChange = (field, value) => {
    setLimitValues((prev) => ({ ...prev, [field]: value }));
    if (limitErrors[field]) {
      setLimitErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSaveLimits = async () => {
    // Saving is the point of this dialog, so both limits are mandatory here
    const errors = validateTokenLimitValues(limitValues, { required: true });
    if (Object.keys(errors).length > 0) {
      setLimitErrors(errors);
      return;
    }

    const payload = buildTokenLimitPayload(limitValues);
    if (!payload) return;

    setSaving(true);
    setNotice(null);
    try {
      await saveClientTokenLimit(api, tenantId, payload);
      setNotice({ severity: 'success', message: 'AI token limits saved.' });
      await load();
      onSaved?.();
    } catch (err) {
      console.error('Error saving AI token limits:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to save AI token limits'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetCycle = async () => {
    const confirmed = await showConfirm(
      `Reset the AI usage cycle for "${client?.TenantName}"? The progress meters restart at 0% and `
        + 'the allowance becomes available again. Recorded usage and cost history are kept.',
      { title: 'Reset AI usage cycle', confirmText: 'Reset' },
    );
    if (!confirmed) return;

    setResetting(true);
    setNotice(null);
    try {
      await resetAiUsageCycle(api, tenantId);
      setNotice({ severity: 'success', message: 'AI usage cycle reset. Historic usage is preserved.' });
      await load();
      onSaved?.();
    } catch (err) {
      console.error('Error resetting AI usage cycle:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to reset the AI usage cycle'));
    } finally {
      setResetting(false);
    }
  };

  const busy = loading || saving || resetting;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: 'min(640px, 85vh)' } }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              AI Usage — {client?.TenantName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {client?.TenantCode}
            </Typography>
          </Box>
          {summary?.isConfigured && (
            <Chip
              label={severityColor.label}
              size="small"
              sx={{
                backgroundColor: severityColor.soft,
                color: severityColor.main,
                fontWeight: 600,
              }}
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {notice && (
          <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice.message}
          </Alert>
        )}

        {loading && !summary ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!summary?.isConfigured && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  No AI token allowance is set for this client yet. Set one below to start metering
                  usage and to show the AI Usage meter to the client&apos;s users.
                </Alert>

                {/* Usage is recorded even without an allowance — showing it here is
                    what tells JLL what allowance this client actually needs. */}
                {byModel.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Usage recorded so far
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Nothing has been charged: without an allowance there is no overage to bill.
                    </Typography>
                    <AiCostGrid rows={byModel} currency={currency} />
                  </>
                )}
              </>
            )}

            {summary?.isConfigured && (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: { xs: 2, md: 4 },
                    mb: 2,
                  }}
                >
                  <TokenUsageBar
                    label="Input tokens"
                    used={summary.inputTokensUsed}
                    limit={summary.inputTokenLimit}
                    percent={summary.inputUsagePercent}
                    alertThresholdPercent={summary.alertThresholdPercent}
                  />
                  <TokenUsageBar
                    label="Output tokens"
                    used={summary.outputTokensUsed}
                    limit={summary.outputTokenLimit}
                    percent={summary.outputUsagePercent}
                    alertThresholdPercent={summary.alertThresholdPercent}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 2, sm: 4 },
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Overage cost this cycle
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {formatCost(summary.totalCost, currency)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      AI calls
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {formatTokens(summary.totalCalls)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Enforcement
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {summary.isEnforced ? 'Blocks at limit' : 'Overage allowed'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Cycle started
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {summary.cycleStartDate
                        ? new Date(summary.cycleStartDate).toLocaleDateString()
                        : '—'}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Cost by model
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Users can switch model per card; each run is billed at that model&apos;s rate.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={allTime}
                        onChange={(event) => setAllTime(event.target.checked)}
                        disabled={busy}
                      />
                    }
                    label={<Typography variant="caption">Include earlier cycles</Typography>}
                  />
                </Box>

                <AiCostGrid
                  rows={byModel}
                  currency={currency}
                  emptyMessage="No AI cards have been run for this client yet."
                />
              </>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {summary?.isConfigured ? 'Adjust limits' : 'Set limits'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Raising a limit takes effect immediately; tokens already billed as overage are not
              refunded.
            </Typography>

            <AiTokenLimitFields
              values={limitValues}
              errors={limitErrors}
              onChange={handleLimitChange}
              disabled={busy}
              showHeading={false}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {summary?.isConfigured && (
          <Tooltip
            arrow
            title="Restart the meters at 0% for a new billing cycle. Usage history is kept."
          >
            <Button
              onClick={handleResetCycle}
              disabled={busy}
              color="warning"
              startIcon={resetting ? <CircularProgress size={16} /> : <ResetIcon />}
              sx={{ mr: 'auto', textTransform: 'none' }}
            >
              Reset usage cycle
            </Button>
          </Tooltip>
        )}
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        <Button
          onClick={handleSaveLimits}
          variant="contained"
          disabled={busy}
          startIcon={saving ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: '#000000',
            '&:hover': { backgroundColor: '#333333' },
          }}
        >
          {saving ? 'Saving...' : 'Save limits'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientAiUsageDialog;
